import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { type PropsWithChildren, useState, useEffect } from "react";

interface FadeEffectProps {
  className?: string;
  shouldShow?: boolean;
}

export const FadeEffect = ({
  children,
  shouldShow = true,
  className,
}: PropsWithChildren<FadeEffectProps>) => {
  const [isReady, setReady] = useState(false);
  // isMounted is kept true for 200ms after shouldShow goes false so the CSS
  // leave transition completes before the node is removed from the DOM.
  const [isMounted, setMounted] = useState(shouldShow);

  useEffect(() => {
    if (shouldShow) {
      setMounted(true);
      // rAF ensures the enter-from state (opacity-0) is painted before we
      // flip to the enter-to state, giving the CSS transition something to animate from.
      const frame = requestAnimationFrame(() => setReady(true));
      return () => cancelAnimationFrame(frame);
    }
    setReady(false);
    const timer = setTimeout(() => setMounted(false), 200);
    return () => clearTimeout(timer);
  }, [shouldShow]);

  if (!isMounted) return null;

  return (
    <div
      className={classNames(
        className,
        isReady
          ? "transition-all duration-500 ease-in-out translate-y-0 opacity-100"
          : "transition-all duration-200 ease-in -translate-y-2 opacity-0",
      )}
    >
      {children}
    </div>
  );
};
